import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ActivityEntityName } from '@jms/model';
import { EntityManifestService } from '@workbuddy/services/entities';
import { CanDeactivateEntityDialog, ENTITY_DIALOG_OUTLET, ENTITY_DIALOG_STATE_ID, EntityDialogDeactivateReason, EntityDialogDeactivateSource, EntityDialogInternal, EntityDialogOperation, EntityDialogRequest, EntityDialogs, EntityDialogSource, toEntityDialogPresentation } from './entity.dialog.contracts';

/**
 * Owns the shell's dialog outlet: one dialog at a time, addressed by URL.
 *
 * **Why routed rather than imperative.** A dialog holding a batched model must survive nothing and
 * block everything: `beforeClose` on an imperative dialog sees Cancel, Escape and backdrop, but it
 * never sees the URL changing underneath it. A side-nav click or browser Back would discard a
 * half-filled form silently. Routing it means `CanDeactivateFn` covers those, and the same leave check
 * answers both exits.
 *
 * **No history stack.** Unlike an inspector, a dialog is not navigable — there is no drilling deeper,
 * so no trail, no position stamping and no branch truncation. Opening one pushes a single entry;
 * closing strips the outlet.
 */
@Injectable()
export class EntityDialogService implements EntityDialogs, EntityDialogDeactivateSource, EntityDialogSource {

  private readonly router = inject(Router);
  private readonly manifests = inject(EntityManifestService);
  private readonly injector = inject(Injector);

  /** Staged until the matching routed host claims invocation-local initial values, source and lifecycle data. */
  private pendingRequest: EntityDialogInternal | null = null;

  private guard: CanDeactivateEntityDialog | null = null;

  private readonly _$active = signal(false);
  private readonly _$deactivating = signal(false);
  private readonly _$deactivatePending = signal(false);

  public readonly $active = this._$active.asReadonly();
  public readonly $deactivating = this._$deactivating.asReadonly();
  public readonly $deactivatePending = this._$deactivatePending.asReadonly();

  /**
   * Opens create or edit on the shell dialog outlet.
   *
   * Resolves operation construction (surface vs custom) for presentation defaults, authorizes, then
   * navigates. Invocation-local `params` / `initialValues` / `afterSave` are staged until the host
   * claims them. Returns false when the entity lacks the operation, authorize denies, leave check
   * refuses to replace a live dialog, or navigation fails.
   */
  public async open(request: EntityDialogRequest): Promise<boolean> {

    // Authorization resolves before a URL exists, so a denied operation opens nothing rather than
    // mounting an empty dialog that tells the user they cannot do something. The record is not loaded
    // here, so record-state rules fall to the surface / custom component; this catches rights-only denials.
    if (!this.manifests.supportsOperation(request.entity, request.operation)) return false;
    if (!await this.manifests.authorizeOperation(request.entity, request.operation, undefined, this.injector)) return false;

    // Replacing a live dialog would destroy a batched model with nothing asking, which is the one
    // transition an inspector got wrong. Refuse instead: the caller decides what to do.
    if (this._$active() && !await this.close()) return false;

    // Presentation defaults from operations.* construction; callers may still override per open.
    const construction = this.manifests.resolveOperation(request.entity, request.operation);
    const presentation = request.presentation ?? toEntityDialogPresentation(construction?.presentation);

    const internal: EntityDialogInternal = { ...request, presentation, dialogId: crypto.randomUUID() };
    this.pendingRequest = internal;

    const slug = this.manifests.slug(request.entity);
    if (!slug) {
      this.pendingRequest = null;
      return false;
    }

    const routeContext = request.id ? [{ id: request.id }] : [];
    // Prefill / host params cannot be reconstructed from the URL after reload. Mark those
    // invocations as transient for both create and edit; seedless routes remain valid deep links.
    const hasInvocationState = request.initialValues !== undefined || request.params !== undefined;
    const state = hasInvocationState
      ? { [ENTITY_DIALOG_STATE_ID]: internal.dialogId }
      : undefined;
    const ok = await this.dialogNav([slug, request.operation, ...routeContext], false, state);
    if (!ok) this.pendingRequest = null;
    return ok;
  }

  /**
   * Runs the leave check, then strips the outlet. Resolves false, without asking or navigating, while a leave check is
   * already pending: that is a Save picked in the leave prompt committing, and the host's close-on-save would otherwise
   * strip the outlet over the navigation the user asked for. The pending navigation (or close) exits once the guard answers.
   */
  public async close(): Promise<boolean> {

    if (this._$deactivatePending()) return false;
    // 'close', not 'navigate': the container is going away with the record unsaved, which is the question
    // the inspector's guard already answers under that name.
    if (!await this.canDeactivateActive('close')) return false;

    // Set across the strip so the route guard does not ask the same question a second time on the
    // navigation this triggers. Cleared in `finally` on every path, so it can never stick.
    this._$deactivating.set(true);
    try {
      return await this.dialogNav(null, true);
    } finally {
      this._$deactivating.set(false);
    }
  }

  /**
   * Asks whatever is mounted. No guard registered means nothing to lose — an empty dialog, or one whose
   * surface has not resolved yet.
   *
   * `$deactivatePending` closes a real race: a Cancel click awaits a confirm, then the user clicks the
   * side nav. Without it the same dialog is asked twice and shows two prompts.
   */
  public async canDeactivateActive(reason: EntityDialogDeactivateReason): Promise<boolean> {

    if (!this.guard) return true;

    this._$deactivatePending.set(true);
    try {
      return await this.guard.canDeactivate(reason);
    } finally {
      this._$deactivatePending.set(false);
    }
  }

  public claimPending(match: { entity: ActivityEntityName; operation: EntityDialogOperation; id?: string; dialogId?: string }): EntityDialogInternal | null {

    const pending = this.pendingRequest;
    if (!pending) return null;
    if (pending.entity !== match.entity || pending.operation !== match.operation || pending.id !== match.id) return null;
    if (match.dialogId && pending.dialogId !== match.dialogId) return null;

    this.pendingRequest = null;
    return pending;
  }

  public registerGuard(guard: CanDeactivateEntityDialog | null): void {

    this.guard = guard;
  }

  /** Called by the host as it mounts and unmounts, so `$active` reflects the DOM rather than intent. */
  public setActive(active: boolean): void {

    this._$active.set(active);
    if (!active) this.guard = null;
  }

  /** Null segments strip the outlet. `replaceUrl` on the strip keeps a closed dialog out of history. */
  private dialogNav(segments: unknown[] | null, replaceUrl = false, state?: Record<string, unknown>): Promise<boolean> {

    return this.router.navigate([{ outlets: { [ENTITY_DIALOG_OUTLET]: segments } }], {
      queryParamsHandling: 'preserve',
      preserveFragment: true,
      replaceUrl,
      ...(state ? { state } : {}),
    });
  }
}
